import classcat from "classcat"
import React, { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { graphql, useFragment } from "react-relay"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./PhaseEditor.module.css"

import { PhaseEditor_project$key } from "~/forms/ProjectBudgetForm/PhaseBudget/__generated__/PhaseEditor_project.graphql"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"

import PhaseIcon from "~/common/PhaseIcon"

import ExpenseBudgetInput from "../ExpenseBudgetInput"
import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import TableInfo from "../TableInfo"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"
import { calculatePhaseBudget } from "@runn/calculations"

type Props = {
  project: PhaseEditor_project$key
}

const PhaseEditor: React.FC<Props> = (props) => {
  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  const { hideFinancials } = useProjectBudgetFormContext()
  const canViewFinancials = !hideFinancials

  const phaseBudgets = formData.phaseBudgets
  const noPhaseBudget = formData.noPhaseBudget

  const totalRevenueBudget = phaseBudgets.reduce(
    (acc, phase) => acc + phase.revenueBudget,
    noPhaseBudget,
  )

  const project = useFragment<PhaseEditor_project$key>(
    graphql`
      fragment PhaseEditor_project on projects {
        id
        no_phase_revenue_budget: no_phase_revenue_budget_private
        phases {
          id
          name
          color
          start_date: start_date_runn
          revenue_budget: revenue_budget_private
        }
      }
    `,
    props.project,
  )

  const noPhaseItem = {
    id: null,
    name: "No Phase",
    color: "var(--no-phase)",
    revenue_budget: noPhaseBudget || 0,
  }

  const handlePhaseUpdate = (phaseId: number, revenueBudget: number) => {
    const updatedPhaseBudgets = [...phaseBudgets]
    const existingIndex = updatedPhaseBudgets.findIndex(
      (b) => b.phaseId === phaseId,
    )
    if (existingIndex !== -1) {
      updatedPhaseBudgets[existingIndex] = { phaseId, revenueBudget }
    } else {
      updatedPhaseBudgets.push({ phaseId, revenueBudget })
    }
    setValue("phaseBudgets", updatedPhaseBudgets)
  }

  const handleNoPhaseUpdate = (budget: number) => {
    setValue("noPhaseBudget", budget)
  }

  const orderedPhases = useMemo(() => {
    return [...project.phases].sort((a, b) => {
      return Number(a.start_date) - Number(b.start_date)
    })
  }, [project.phases])

  return (
    <div className={commonStyles.phaseEditorWrapper} data-test="phase-editor">
      <Row heading>
        <Column>Phase</Column>
        {canViewFinancials && <Column textAlign="right">Phase Budget</Column>}
      </Row>

      <TableInfo className={styles.tableBody}>
        {orderedPhases.map((phase) => {
          const phaseBudget = phaseBudgets.find(
            (budget) => budget.phaseId === phase.id,
          )
          const value =
            phaseBudget?.revenueBudget ||
            calculatePhaseBudget({
              budgetMethod: "PHASE",
              phase,
            })
          return (
            <Row key={phase.id}>
              <Column className={styles.phaseName}>
                <PhaseIcon size={20} color={phase.color} />
                {phase.name}
              </Column>
              {canViewFinancials && (
                <Column>
                  <ExpenseBudgetInput
                    name={`phase-budget-${phase.name}`}
                    currency={currency}
                    onUpdate={(v) => handlePhaseUpdate(phase.id, v)}
                    value={value || 0}
                    maxWidth={110}
                  />
                </Column>
              )}
            </Row>
          )
        })}
        <Row>
          <Column className={styles.phaseName}>
            <PhaseIcon size={20} color={noPhaseItem.color} />
            {noPhaseItem.name}
          </Column>
          {canViewFinancials && (
            <Column>
              <ExpenseBudgetInput
                name="phase-budget-no-phase"
                currency={currency}
                onUpdate={handleNoPhaseUpdate}
                value={noPhaseItem.revenue_budget}
                maxWidth={110}
              />
            </Column>
          )}
        </Row>
      </TableInfo>
      {canViewFinancials && (
        <TableInfo
          sticky
          className={classcat([
            commonStyles.totalsWrapper,
            styles.totalsWrapper,
          ])}
          offset={0}
        >
          <div>Phase Budget</div>
          <div data-test="total-phase-budget">
            {formatCurrency(totalRevenueBudget)}
          </div>
        </TableInfo>
      )}
    </div>
  )
}

export default PhaseEditor
